/**
 * Shared Estimate Page
 *
 * Displays a previously saved estimate from a shareable link.
 */

import { Trans, useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { EstimateOutput, WizardState, RetailerStatus } from '../types';
import { MaterialsList } from '../components/results/MaterialsList';
import { ActionButtons } from '../components/results/ActionButtons';
import { ExportDataButton } from '../components/results/ExportDataButton';
import { PlacementGuide } from '../components/results/PlacementGuide';
import { CustomTablesSection } from '../components/results/CustomTablesSection';
import { LocationMapSection } from '../components/maps/LocationMapSection';
import { OutputSectionsSection } from '../components/results/OutputSectionsSection';
import { ResultsVisualCard } from '../components/results/ResultsVisualCard';
import { CommentsSection } from '../components/comments/CommentsSection';
import { loadEstimateByGuid, repriceEstimate } from '../services/api';
import { loadProducts, getLoadedRetailerId } from '../data/productTable';
import { doEstimate } from '../calculations';
import { useCurrency } from '../hooks/useCurrency';
import { useSharedEstimateView } from '../context/SharedEstimateViewContext';
import { usePartnerBranding } from '../context/PartnerBrandingContext';
import { useLanguage } from '../context/LanguageContext';
import { currencyPolicy } from '../utils/currencyPolicy';
import { setTrackingContext } from '../utils/gtmTracking';
import { markSplashSeen as persistSplashSeen } from '../utils/splashSeen';
import './SharedEstimate.css';

export const SharedEstimate: React.FC = () => {
  const { t } = useTranslation('results');
  const { id } = useParams<{ id: string }>();
  const [estimate, setEstimate] = useState<EstimateOutput | null>(null);
  const [wizardState, setWizardState] = useState<WizardState | null>(null);
  const [estimateName, setEstimateName] = useState<string>('');
  const [showMsrp, setShowMsrp] = useState<boolean>(false);
  const [retailerStatus, setRetailerStatus] = useState<RetailerStatus | null>(null);
  // ISO date the displayed pricing was computed — drives the "valid for 30 days
  // from …" note on the disclaimer.
  const [pricedAt, setPricedAt] = useState<string | null>(null);
  // Set when the estimate's origin retailer is gone/paused and we've fallen
  // back to Trusscore MSRP — drives the friendly explanatory notice.
  const [retailerUnavailable, setRetailerUnavailable] = useState<{ name: string | null; kind: 'deleted' | 'deactivated' | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleted, setDeleted] = useState(false);
  // Tiered currency detection: session override → IP geolocate → browser
  // locale → USD default. Matches the behavior on the live estimator page.
  const { currency, setCurrency } = useCurrency();
  const { setView } = useSharedEstimateView();
  const { partner, applyPartnerSlug } = usePartnerBranding();
  const { applyContentLocale, releaseContentLocale } = useLanguage();
  // Held in refs, not listed as dependencies: both are memoized on the locale
  // registry, which arrives on its own schedule, and naming them would re-run
  // the whole estimate fetch when it lands.
  const applyContentLocaleRef = useRef(applyContentLocale);
  const releaseContentLocaleRef = useRef(releaseContentLocale);
  useEffect(() => { applyContentLocaleRef.current = applyContentLocale; }, [applyContentLocale]);
  useEffect(() => { releaseContentLocaleRef.current = releaseContentLocale; }, [releaseContentLocale]);

  // Hand the language back when this estimate is no longer on screen, so
  // reading one French estimate does not leave the rest of the session French.
  useEffect(() => () => { releaseContentLocaleRef.current(); }, []);
  // Per-estimate manager flag OR the retailer-wide "always show totals" override
  // (the latter keyed to the active partner branding — shared estimates + PDF).
  const effectiveShowPrices = showMsrp || !!partner?.showTotals;

  // Single-currency retailer: force that currency (no toggle anywhere).
  const { forced: forcedCurrency } = currencyPolicy(partner?.currenciesSupported);
  useEffect(() => {
    if (forcedCurrency && currency !== forcedCurrency) setCurrency(forcedCurrency);
  }, [forcedCurrency, currency, setCurrency]);

  // Analytics: price keys report the figure on screen, so the display
  // currency and the retailer's rounding must be known before any event.
  useEffect(() => {
    setTrackingContext({ currency, totalFormat: partner?.totalFormat ?? 'rounded' });
  }, [currency, partner?.totalFormat]);

  /**
   * Mark the apex splash as seen so it won't greet anyone who hits "Create
   * New Estimate" — a deliberate, already-onboarded action. The flag now
   * lives in localStorage (see utils/splashSeen), so the suppression holds
   * across the new-tab boundary the admin panel opens estimates in.
   */
  const markSplashSeen = () => persistSplashSeen(null);

  /**
   * Expose the viewed estimate to the app-level Header so it can offer
   * context-sensitive menu items: "Save Current Config" (downloads the
   * viewed estimate) and "New Estimate from this template" (clones the
   * config minus personal info).
   */
  useEffect(() => {
    if (wizardState) {
      setView({ wizardState, estimateName });
    }
    return () => setView(null);
  }, [wizardState, estimateName, setView]);

  useEffect(() => {
    const fetchEstimate = async () => {
      if (!id) {
        setError(t('shared.noId'));
        setLoading(false);
        return;
      }

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const source = urlParams.get('source') || undefined;
        // Retailer-share recipient guid — when present, the API resolves the
        // retailer attached to the share and returns retailerStatus so we can
        // surface the "retailer no longer available" banner if it's been
        // deleted since the share was sent.
        const recipient = urlParams.get('recipient') || undefined;
        const data = await loadEstimateByGuid(id, source, recipient);

        if (data.status === 'completed' && data.estimateOutput) {
          let output = data.estimateOutput as EstimateOutput;
          let pricedAtIso = data.pricedAt ?? null;

          if (data.pricing?.needsReprice) {
            // The snapshot is >30 days old (or its retailer's availability
            // changed) — recompute against current pricing for the resolved
            // source (the origin retailer if still active, else Trusscore #1
            // MSRP), display it, and persist it back to the SAME record. The
            // URL never changes; the customer just sees fresh numbers.
            await loadProducts(data.pricing.source, true).catch(() => false);
            try {
              const fresh = doEstimate(output.input);
              // Keep the estimate's identity + lead info; only pricing refreshes.
              fresh.createdAt = output.createdAt;
              fresh.id = output.id;
              fresh.projectInfo = output.projectInfo;
              fresh.userNotes = output.userNotes;
              fresh.currency = output.currency;
              output = fresh;
              pricedAtIso = new Date().toISOString();
              // Fire-and-forget: the server re-checks staleness before writing.
              repriceEstimate(id, fresh).catch(() => { /* fresh pricing already shown */ });
            } catch {
              // Recompute failed (e.g. a product went missing) — fall back to
              // the stored snapshot so the page still renders.
              output = data.estimateOutput as EstimateOutput;
              pricedAtIso = data.pricedAt ?? null;
            }
          } else {
            // Not repricing — still resolve custom-field columns + values LIVE
            // for the estimate's retailer (SKUs/links stay current on shared
            // links, never snapshotted). Precedence: share recipient retailer,
            // then the branded portal, else apex/Trusscore #1.
            const retId = data.retailerStatus?.id ?? data.originRetailer?.id ?? null;
            if (retId && retId !== getLoadedRetailerId()) {
              await loadProducts(retId, true).catch(() => false);
            }
          }

          // Branding follows the data: re-apply the portal the estimate was
          // created in so a bare /estimate/<guid> link keeps its partner skin,
          // OR revert to the Trusscore default when the estimate is unbranded
          // (otherwise a previously-viewed partner theme would linger). No-ops
          // when a URL/subdomain partner already won at boot.
          applyPartnerSlug(data.originRetailer?.slug ?? null);
          // Show the estimate in the language it was written in. Unlike
          // currency — which follows the viewer, because a price is only
          // meaningful in the money they spend — language follows the estimate:
          // a French contractor sending a materials list to their client meant
          // to send a French materials list. The switcher stays available, and
          // a visitor who has already picked a language keeps it.
          applyContentLocaleRef.current(data.locale);
          setEstimate(output);
          setPricedAt(pricedAtIso);
          setRetailerUnavailable(
            data.pricing?.unavailable
              ? { name: data.pricing.retailerName, kind: data.pricing.fallbackKind }
              : null,
          );
          setWizardState(data.wizardState ?? null);
          setEstimateName(data.estimateName || '');
          setRetailerStatus(data.retailerStatus ?? null);
          // Phase 1: only the per-estimate creator-visibility flag is honored.
          // Phase 2 will layer in recipient/retailer-aware resolution:
          //   shouldShow = data.showMsrpToCreator
          //             || (recipient?.retailerId && retailerHasPricing(...))
          setShowMsrp(Boolean(data.showMsrpToCreator));
          // Note: we intentionally do NOT force-apply
          // data.estimateOutput.currency here. The viewer of a shared link
          // should see prices in THEIR local currency (via useCurrency's
          // tiered auto-detection), not the currency the original creator
          // happened to save the estimate in. They can still toggle.
        } else {
          setError(t('shared.incomplete'));
        }
      } catch (err) {
        // A soft-deleted estimate comes back as a flagged error (410) — show
        // the friendly "deleted" state rather than a generic load failure.
        if ((err as { deleted?: boolean })?.deleted) {
          setDeleted(true);
        } else {
          const message = err instanceof Error ? err.message : t('shared.loadFailed');
          setError(message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEstimate();
    // applyPartnerSlug is memoized on the boot slug (stable for the page life),
    // so it won't re-trigger the fetch.
  }, [id, applyPartnerSlug]);

  if (loading) {
    return (
      <div className="shared-estimate loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('shared.loading')}</p>
        </div>
      </div>
    );
  }

  if (deleted) {
    return (
      <div className="shared-estimate error">
        <div className="error-content">
          <div style={{ fontSize: 64, lineHeight: 1, marginBottom: 8 }} role="img" aria-label={t('shared.vanishedAlt')}>
            🫥
          </div>
          <h1>{t('shared.deletedTitle')}</h1>
          <p>{t('shared.deletedBody')}</p>
          <Link to="/" className="btn btn-primary" onClick={markSplashSeen}>
            {t('shared.createYourOwn')}
          </Link>
        </div>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="shared-estimate error">
        <div className="error-content">
          <div className="error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h1>{t('shared.notFoundTitle')}</h1>
          <p>{error || t('shared.notFoundBody')}</p>
          <Link to="/" className="btn btn-primary" onClick={markSplashSeen}>
            {t('shared.createNew')}
          </Link>
        </div>
      </div>
    );
  }

  // Header summary line. Mirrors the ROOM CONFIGURATION block:
  //  - wall-by-wall has no single room size → "Custom configuration"
  //  - ceiling-only with >1 zone → "Multiple Zones"
  //  - otherwise the room's L × W × H
  const { calculationMode, roomDimensions, ceilingZones } = estimate.input;
  const roomSummary =
    calculationMode === 'IndividualWalls'
      ? t('config.customConfiguration')
      : calculationMode === 'CeilingOnly' && ceilingZones && ceilingZones.length > 1
        ? t('config.multipleZones')
        : t('shared.roomSummary', {
            length: roomDimensions.length,
            width: roomDimensions.width,
            height: roomDimensions.height,
          });

  return (
    <div className="shared-estimate">
      {/* Top row — tag (left) + Create Your Own CTA (right), single line on all sizes */}
      <div className="shared-top-row">
        <span className="shared-tag">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
          </svg>
          {t('shared.badge')}
        </span>
        <Link to="/" className="btn btn-primary btn-sm shared-cta" onClick={markSplashSeen}>
          {t('shared.createNew')}
        </Link>
      </div>

      {/* Stale-retailer banner — renders only when the original sending
          retailer has been deleted since this estimate was shared. The
          pricing shown reflects values snapshotted at share-time. */}
      {retailerStatus?.isStale && (
        <div
          role="alert"
          style={{
            background: '#dc2626',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: 8,
            margin: '12px 0 16px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            boxShadow: '0 2px 6px rgba(220,38,38,0.25)',
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="22" height="22" style={{ flexShrink: 0, marginTop: 2 }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9"  x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div style={{ lineHeight: 1.4 }}>
            <div style={{ fontSize: 16 }}>
              <Trans i18nKey="shared.retailerGoneTitle" ns="results"
                values={{ retailer: retailerStatus.name }}
                components={{ u: <span style={{ textDecoration: 'underline' }} /> }} />
            </div>
            <div style={{ fontWeight: 500, fontSize: 13, marginTop: 4, opacity: 0.95 }}>
              {t('shared.retailerGoneBody')}
            </div>
          </div>
        </div>
      )}

      {/* Retailer-unavailable notice — friendly, informational (not the red
          alarm banner above). Shown when the estimate's original retailer has
          been removed or paused, so pricing was refreshed at Trusscore MSRP. */}
      {retailerUnavailable && (
        <div
          role="status"
          style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            color: '#1e3a5f',
            padding: '12px 16px',
            borderRadius: 8,
            margin: '12px 0 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            {/* One whole sentence per case rather than a base string with two
                interpolated fragments: "no longer active/listed" does not
                survive being assembled from parts in French, where the
                adjective agrees and moves. */}
            <Trans
              i18nKey={retailerUnavailable.kind === 'deactivated'
                ? 'shared.retailerDeactivated'
                : 'shared.retailerDelisted'}
              ns="results"
              values={{ retailer: retailerUnavailable.name || t('shared.theRetailer') }}
              components={{ b: <strong /> }}
            />
          </div>
        </div>
      )}

      {/* Results Header */}
      <div className="results-header">
        <div className="results-header-content">
          {estimateName && (
            <h1 className="results-project-name">{estimateName}</h1>
          )}
          <h2 className={estimateName ? 'results-title results-title--sub' : 'results-title'}>{t('defaultTitle')}</h2>
          <p className="results-subtitle">
            {roomSummary}
          </p>
          <p className="results-date">
            {t('shared.created', { date: new Date(estimate.createdAt).toLocaleDateString() })}
          </p>
        </div>
      </div>

      {/* Results visual card — price + configs + bitmap visualizer */}
      <ResultsVisualCard
        estimateResult={estimate}
        wizardState={wizardState}
        currency={currency}
        onCurrencyChange={setCurrency}
        pulseKey={`${estimate.id}-${currency}`}
        totalFormat={partner?.totalFormat ?? 'rounded'}
        pricedAt={pricedAt}
      />

      {/* Main Content Grid — materials list + action buttons (price moved to card above) */}
      <div className="results-grid">
        <div className="results-main">
          <MaterialsList
            billOfMaterials={estimate.billOfMaterials}
            currency={currency}
            showPrices={effectiveShowPrices}
            headerActions={
              <ExportDataButton estimateId={estimate.id} estimate={estimate} estimateName={estimateName} />
            }
          />
        </div>

        <div className="results-sidebar">
          {/* A shared estimate is always saved — its identifier is the URL GUID.
              estimate.id (the output's optional id) may be empty, so fall back to
              the GUID; otherwise `requiresSavedEstimate` buttons stay hidden here. */}
          <ActionButtons estimateId={estimate.id || id} estimate={estimate} overrideCurrency={currency} showPrices={effectiveShowPrices} wizardState={wizardState ?? undefined} totalFormat={partner?.totalFormat ?? 'rounded'} estimateName={estimateName} pricedAt={pricedAt} />
        </div>
      </div>

      {/* Custom tables below the BOM */}
      <CustomTablesSection estimate={estimate} placement="belowBom" />

      {/* Inline location map (retailers / installers) — screen only, never print/PDF */}
      <LocationMapSection placement="belowBom" estimateGuid={id ?? null} retailerId={getLoadedRetailerId() || partner?.id || null} />

      {/* Product Placement Guide */}
      <PlacementGuide estimateResult={estimate} />

      {/* Custom tables below the placement guide */}
      <CustomTablesSection estimate={estimate} placement="belowPlacement" />

      {/* Output footer sections (admin-authored; screen / print, mirrored in the PDF) */}
      <OutputSectionsSection />

      {/* Comments */}
      {id && <CommentsSection estimateGuid={id} />}
    </div>
  );
};

export default SharedEstimate;
